import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useUnit } from "@/context/UnitContext";
import { UnitToggle } from "@/components/UnitToggle";
import { mToUnit, unitToM, unitLabel, dimPairLabel } from "@/lib/units";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Plus, Copy, Trash2, GripVertical, Home, Info, Sparkles,
  Truck, Wrench, PackageCheck, HandHeart, Ruler, CheckCircle2, Save, Loader2, X,
} from "lucide-react";
import { toast } from "sonner";
import PublicHeader from "@/components/PublicHeader";
import SendRequestModal from "@/components/SendRequestModal";
import { EmailResumeLink } from "@/components/EmailResumeLink";
import { RoomMatchCard } from "@/components/RoomMatchCard";
import { api, gbp, floorLabel } from "@/lib/api";
import useRequestsConfig from "@/lib/useRequestsConfig";
import { track, captureUtm, getUtm } from "@/lib/analytics";
import Seo from "@/seo/Seo";
import {
  PROPERTY_TYPES, ROOM_TYPES, ROOM_NAMES, STAIRS_NAMES, FLOORING_OPTIONS, COLOUR_OPTIONS,
  TEXTURE_OPTIONS, STYLE_GOALS, SERVICE_OPTIONS, DEFAULT_ROOM,
} from "@/lib/wholeHome";

const STEPS = ["property", "rooms", "style", "results", "service", "requirements", "review"];

const EMPTY = {
  property_type: "house",
  postcode: "",
  project_name: "",
  rooms: [DEFAULT_ROOM()],
  preferences: { style_goal: "", colour: "", texture: "" },
};

const SERVICE_ICON = {
  collect_self: PackageCheck,
  collection_delivery: Truck,
  fitting: Wrench,
  collection_delivery_fitting: HandHeart,
};

export default function WholeHome() {
  const nav = useNavigate();
  const { user: authUser } = useAuth();
  // Per-account draft scoping — never leak a prior signed-in account's private
  // plan to another account on a shared device. Anonymous use is namespaced to
  // "guest"; a signed-in user only ever sees their own draft.
  const draftScope = authUser?.id || "guest";
  const dataKey = `fm_whole_home.${draftScope}`;
  const stepKey = `fm_whole_home_step.${draftScope}`;
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [step, setStep] = useState("property");
  const [data, setData] = useState(EMPTY);

  // Load the correct per-account draft once auth resolves; purge the legacy
  // global keys that predate scoping (one-time migration against leaks).
  useEffect(() => {
    try {
      localStorage.removeItem("fm_whole_home");
      localStorage.removeItem("fm_whole_home_step");
    } catch (e) { /* ignore */ }
    try {
      const d = JSON.parse(localStorage.getItem(dataKey) || "null");
      setData(d ? { ...EMPTY, ...d } : EMPTY);
      const s = localStorage.getItem(stepKey);
      setStep(["results", "service", "requirements", "review"].includes(s) ? "style" : (s || "property"));
    } catch (e) { setData(EMPTY); setStep("property"); }
    setDraftLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftScope]);
  const [matchResult, setMatchResult] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [service, setService] = useState("");
  const [reqs, setReqs] = useState({});
  const [serviceEstimate, setServiceEstimate] = useState(null);
  const [savedRef, setSavedRef] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  // Bring the shopper back to their request review step after sign-in; the
  // room plan itself is restored from localStorage.
  useEffect(() => {
    if (!authUser || authUser.role !== "customer" || authUser.email_verified === false) return;
    try {
      if (sessionStorage.getItem("fm_request_intent")) {
        sessionStorage.removeItem("fm_request_intent");
        setShowRequest(true);
      }
    } catch (e) { /* ignore */ }
  }, [authUser]);
  const [params] = useSearchParams();
  const requestsCfg = useRequestsConfig();

  useEffect(() => { captureUtm(); track("whole_home_started"); }, []);

  // Demonstration mode: load a ready-made whole-home example.
  useEffect(() => {
    if (params.get("demo") !== "1") return;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/whole-home/demo");
        const d = res.data;
        setData({
          property_type: d.property_type || "house",
          postcode: d.postcode || "",
          project_name: d.project_name || "Demonstration home",
          rooms: (d.strategies?.[0]?.rooms || []).map((r) => ({
            id: r.room_id, name: r.room_name, width_m: r.width_m, length_m: r.length_m,
            flooring_type: r.flooring_type, budget_gbp: "", colour: "", texture: "", notes: "", is_stairs: r.is_stairs,
          })),
          preferences: { style_goal: "best_saving", colour: "grey", texture: "" },
        });
        setMatchResult({ strategies: d.strategies, any_unmatched: d.any_unmatched });
        setActiveKey(d.strategies?.[0]?.key || null);
        setService(d.preselected_service || "collection_delivery_fitting");
        setServiceEstimate(d.service_estimate || null);
        setIsDemo(true);
        setStep("results");
        track("demo_project_opened");
      } catch (e) {
        toast.error("Couldn't load the demonstration");
      } finally { setLoading(false); }
    })();
  }, [params]);

  // Resume a saved plan from an emailed link (?resume=<token>).
  useEffect(() => {
    const token = params.get("resume");
    if (!token || !draftLoaded) return;
    api.get(`/whole-home/resume/${encodeURIComponent(token)}`)
      .then(({ data: { project: p } }) => {
        setData({
          ...EMPTY,
          property_type: p.property_type || "house",
          postcode: p.postcode || "",
          project_name: p.project_name || "",
          rooms: (p.rooms || []).map((r) => ({ ...r, budget_gbp: r.budget_gbp ?? "", colour: r.colour || "", texture: r.texture || "", notes: r.notes || "" })),
          preferences: { ...EMPTY.preferences, ...(p.preferences || {}) },
        });
        setSavedRef(p.reference);
        setService(p.service || "");
        setReqs(p.service_requirements || {});
        setStep("style");
        track("whole_home_project_resumed", { reference: p.reference });
        toast.success("Welcome back — tap Find flooring to see today's stock for your rooms.");
      })
      .catch(() => toast.error("That resume link has expired or isn't valid."));
  }, [params, draftLoaded]);

  useEffect(() => {
    if (!draftLoaded) return;
    try { localStorage.setItem(dataKey, JSON.stringify(data)); } catch (e) { /* ignore */ }
  }, [data, dataKey, draftLoaded]);
  useEffect(() => {
    if (!draftLoaded) return;
    try { localStorage.setItem(stepKey, step); } catch (e) { /* ignore */ }
  }, [step, stepKey, draftLoaded]);

  const set = (patch) => setData((d) => ({ ...d, ...patch }));
  const setRoom = (id, patch) => setData((d) => ({ ...d, rooms: d.rooms.map((r) => (r.id === id ? { ...r, ...patch } : r)) }));

  function addRoom(name) {
    const r = DEFAULT_ROOM();
    r.name = name || `Room ${data.rooms.length + 1}`;
    r.is_stairs = STAIRS_NAMES.includes(r.name.toLowerCase());
    setData((d) => ({ ...d, rooms: [...d.rooms, r] }));
    track("room_added", { name: r.name });
    return r.id;
  }
  function duplicateRoom(id) {
    const src = data.rooms.find((r) => r.id === id);
    if (!src) return;
    const copy = { ...src, id: DEFAULT_ROOM().id, name: `${src.name} (copy)` };
    setData((d) => {
      const idx = d.rooms.findIndex((r) => r.id === id);
      const next = [...d.rooms];
      next.splice(idx + 1, 0, copy);
      return { ...d, rooms: next };
    });
  }
  function removeRoom(id) {
    setData((d) => ({ ...d, rooms: d.rooms.filter((r) => r.id !== id) }));
  }
  function moveRoom(id, dir) {
    setData((d) => {
      const idx = d.rooms.findIndex((r) => r.id === id);
      const j = idx + dir;
      if (idx < 0 || j < 0 || j >= d.rooms.length) return d;
      const next = [...d.rooms];
      [next[idx], next[j]] = [next[j], next[idx]];
      return { ...d, rooms: next };
    });
  }

  const validRooms = data.rooms.filter((r) => Number(r.width_m) > 0 && Number(r.length_m) > 0);

  async function runMatch() {
    if (validRooms.length === 0) { toast.error("Add at least one room with width and length"); return; }
    setLoading(true);
    track("matching_started", { rooms: validRooms.length });
    try {
      const payload = {
        postcode: data.postcode || null,
        rooms: validRooms.map((r) => ({
          id: r.id, name: r.name,
          width_m: Number(r.width_m), length_m: Number(r.length_m),
          flooring_type: r.flooring_type,
          budget_gbp: r.budget_gbp ? Number(r.budget_gbp) : null,
          colour: r.colour || null, texture: r.texture || null,
          notes: r.notes || null, is_stairs: !!r.is_stairs,
        })),
        preferences: data.preferences,
      };
      const res = await api.post("/whole-home/match", payload);
      setMatchResult(res.data);
      setActiveKey(res.data.strategies?.[0]?.key || null);
      setOverrides({});
      track("matching_results_viewed", { strategies: res.data.strategies?.length || 0 });
      setStep("results");
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error("Couldn't match right now, please try again.");
    } finally {
      setLoading(false);
    }
  }

  const activeStrategy = useMemo(
    () => matchResult?.strategies?.find((s) => s.key === activeKey) || matchResult?.strategies?.[0],
    [matchResult, activeKey],
  );

  const displayedRooms = useMemo(() => {
    if (!activeStrategy) return [];
    return activeStrategy.rooms.map((r) =>
      overrides[r.room_id] ? { ...r, match: overrides[r.room_id], _overridden: true } : r,
    );
  }, [activeStrategy, overrides]);

  const summary = useMemo(() => {
    const matched = displayedRooms.filter((r) => r.match);
    const subtotal = matched.reduce((a, r) => a + (r.match.room_cost_gbp || 0), 0);
    const retail = matched.reduce((a, r) => a + (r.match.retail_estimate_gbp || 0), 0);
    const points = new Set(matched.map((r) => r.collection_point || `x-${r.match.listing.id}`)).size;
    const maxDist = matched.reduce((a, r) => Math.max(a, r.match.distance_miles || 0), 0);
    const saving = Math.max(0, retail - subtotal);
    return {
      matched: matched.length,
      total: displayedRooms.length,
      subtotal: Math.round(subtotal * 100) / 100,
      retail: Math.round(retail * 100) / 100,
      points,
      maxDist,
      saving: Math.round(saving * 100) / 100,
      savingPct: retail > 0 ? Math.round((saving / retail) * 100) : 0,
      area: displayedRooms.reduce((a, r) => a + (r.area_m2 || 0), 0),
    };
  }, [displayedRooms]);

  function replaceMatch(roomId, altCard) {
    setOverrides((o) => ({ ...o, [roomId]: altCard }));
    setServiceEstimate(null);
    track("alternative_product_selected", { room: roomId });
    toast.success("Match updated");
  }
  function selectStrategy(key) {
    setActiveKey(key);
    setOverrides({});
    setServiceEstimate(null);
    track("matching_strategy_selected", { strategy: key });
  }

  async function chooseService(value) {
    setService(value);
    setReqs({});
    setServiceEstimate(null);
    track("service_option_selected", { service: value });
    if (value === "collect_self") {
      setServiceEstimate({ estimate_gbp: 0, lines: [], is_estimate: true, note: "You collect the flooring yourself — no service cost." });
      setStep("review"); window.scrollTo(0, 0); return;
    }
    setStep("requirements"); window.scrollTo(0, 0);
    track("service_request_started", { service: value });
  }

  async function submitRequirements() {
    setLoading(true);
    try {
      const res = await api.post("/whole-home/service-estimate", {
        service,
        rooms: validRooms.map((r) => ({ id: r.id, name: r.name, width_m: Number(r.width_m), length_m: Number(r.length_m), flooring_type: r.flooring_type })),
        requirements: reqs,
        collection_points: summary.points,
        max_distance_miles: summary.maxDist,
      });
      setServiceEstimate(res.data);
      track("service_request_submitted", { service });
      setStep("review"); window.scrollTo(0, 0);
    } catch (e) {
      toast.error("Couldn't estimate service cost");
    } finally { setLoading(false); }
  }

  async function saveProject() {
    setLoading(true);
    const utm = getUtm();
    const selections = {};
    displayedRooms.forEach((r) => { if (r.match) selections[r.room_id] = r.match.listing.id; });
    try {
      const res = await api.post("/whole-home/projects", {
        reference: savedRef,
        project_name: data.project_name || null,
        property_type: data.property_type,
        postcode: data.postcode || null,
        rooms: validRooms.map((r) => ({ id: r.id, name: r.name, width_m: Number(r.width_m), length_m: Number(r.length_m), flooring_type: r.flooring_type, budget_gbp: r.budget_gbp ? Number(r.budget_gbp) : null, colour: r.colour || null, texture: r.texture || null, notes: r.notes || null, is_stairs: !!r.is_stairs })),
        preferences: data.preferences,
        strategy_key: activeKey,
        selections,
        service: service || null,
        service_requirements: reqs,
        service_estimate: serviceEstimate,
        summary,
        source: utm.source || null,
        campaign: utm.campaign || null,
      });
      setSavedRef(res.data.reference);
      track("whole_home_project_saved", { reference: res.data.reference });
      toast.success(`Project saved · ${res.data.reference}`);
    } catch (e) {
      toast.error("Couldn't save project");
    } finally { setLoading(false); }
  }

  async function payForAll() {
    if (isDemo) { toast.info("Checkout is disabled in this demonstration."); return; }
    const verified = authUser && authUser.role === "customer" && authUser.email_verified !== false;
    if (!verified) {
      try { sessionStorage.setItem("fm_checkout_intent", "1"); } catch (e) { /* ignore */ }
      toast.info("Please sign in to pay for your whole plan securely.");
      nav("/account");
      return;
    }
    setLoading(true);
    try {
      const utm = getUtm();
      const selections = {};
      displayedRooms.forEach((r) => { if (r.match) selections[r.room_id] = r.match.listing.id; });
      if (!Object.keys(selections).length) { toast.error("No matched products to pay for yet."); setLoading(false); return; }
      const save = await api.post("/whole-home/projects", {
        reference: savedRef,
        project_name: data.project_name || null,
        property_type: data.property_type,
        postcode: data.postcode || null,
        rooms: validRooms.map((r) => ({ id: r.id, name: r.name, width_m: Number(r.width_m), length_m: Number(r.length_m), flooring_type: r.flooring_type, budget_gbp: r.budget_gbp ? Number(r.budget_gbp) : null, colour: r.colour || null, texture: r.texture || null, notes: r.notes || null, is_stairs: !!r.is_stairs })),
        preferences: data.preferences,
        strategy_key: activeKey,
        selections,
        service: service || null,
        service_requirements: reqs,
        service_estimate: serviceEstimate,
        summary,
        source: utm.source || null,
        campaign: utm.campaign || null,
      });
      const ref = save.data.reference;
      setSavedRef(ref);
      const res = await api.post(`/projects/${ref}/checkout`, { origin_url: window.location.origin });
      try {
        sessionStorage.setItem(`fm.order.${res.data.session_id}`, JSON.stringify({ order_id: res.data.order_id, access_token: res.data.access_token }));
      } catch (e) { /* ignore */ }
      track("combined_checkout_started", { reference: ref, order_id: res.data.order_id, retailers: res.data.retailer_count });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail) toast.error(detail, { duration: 9000 });
      else if (e?.response?.status === 401 || e?.response?.status === 403) {
        try { sessionStorage.setItem("fm_checkout_intent", "1"); } catch (err) { /* ignore */ }
        toast.info("Please sign in to pay for your whole plan securely.");
        nav("/account");
      } else toast.error("Couldn't start combined checkout. Please try again.");
      setLoading(false);
    }
  }


  async function requestFitter({ measuring = false } = {}) {
    if (isDemo) { toast.info("Fitter requests are disabled in this demonstration."); return; }
    if (!data.postcode) { toast.error("Add your postcode first so we can find nearby fitters."); return; }
    const verified = authUser && authUser.role === "customer" && authUser.email_verified !== false;
    if (!verified) {
      try { sessionStorage.setItem("fm_fitter_intent", "1"); } catch (e) { /* ignore */ }
      toast.info("Please sign in with a verified account to request a fitter.");
      nav("/account?next=/whole-home");
      return;
    }
    const jobType = measuring ? "measuring"
      : service === "fitting" ? "fitting"
      : service === "collection_delivery_fitting" ? "collection_fitting"
      : service === "collection_delivery" ? "delivery"
      : "collection_fitting";
    setLoading(true);
    try {
      const res = await api.post("/opportunities", {
        postcode: data.postcode,
        rooms: validRooms.map((r) => ({ name: r.name || "Room", width_m: Number(r.width_m), length_m: Number(r.length_m), flooring_type: r.flooring_type || null })),
        job_type: jobType,
        services_required: measuring ? ["measuring_service"] : [],
        project_reference: savedRef || null,
        source: "whole_home",
      });
      track("fitter_request_created", { reference: res.data.opportunity?.reference_code, job_type: jobType });
      toast.success(measuring
        ? "Measuring request sent to nearby fitters — track it under My account."
        : "Request sent to nearby fitters — track it under My account.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        try { sessionStorage.setItem("fm_fitter_intent", "1"); } catch (err) { /* ignore */ }
        toast.info("Please sign in with a verified customer account to request a fitter.");
        nav("/account?next=/whole-home");
      } else if (e?.response?.status === 429 && detail) {
        toast.error(detail, { duration: 8000 });
      } else {
        toast.error(detail || "Couldn't send your fitter request. Please try again.");
      }
    } finally { setLoading(false); }
  }

  function clearProject() {
    if (!window.confirm("Start again? This clears all rooms and matches on this device.")) return;
    localStorage.removeItem(dataKey);
    localStorage.removeItem(stepKey);
    setData(EMPTY); setMatchResult(null); setOverrides({}); setService(""); setReqs({});
    setServiceEstimate(null); setSavedRef(null); setStep("property");
    window.scrollTo(0, 0);
  }

  const serviceTotal = (summary.subtotal || 0) + (serviceEstimate?.estimate_gbp || 0);

  return (
    <>
      <Seo title="Floor your whole home for less — FloorMatch multi-room matching"
        description="Add your room sizes and FloorMatch finds suitable remnants, roll ends and surplus flooring nearby for one room or your entire home. Collect it yourself or arrange collection, delivery and professional fitting."
        path="/whole-home" />
      <PublicHeader />
      <main className="min-h-screen bg-slate-50 pb-32 md:pb-16" data-testid="whole-home-page">
        {/* Progress header */}
        <div className="bg-[#0F3D2E] text-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h1 className="font-display font-bold text-xl sm:text-2xl text-white">Floor one room or your entire home for less</h1>
                <p className="text-white/75 text-sm mt-1 hidden sm:block">
                  Add your room sizes and we'll find suitable surplus flooring nearby. Collect it yourself or arrange delivery and fitting.
                </p>
              </div>
              <button onClick={clearProject} className="text-xs text-white/70 hover:text-white underline shrink-0" data-testid="clear-project">
                Start again
              </button>
            </div>
            <StepBar step={step} />
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          {isDemo && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm flex items-start gap-2 mb-5" data-testid="demo-banner">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <span><strong>FloorMatch demonstration</strong> — no real stock or booking is being created. This example shows several rooms matched to surplus flooring, with collection, delivery and professional fitting selected.</span>
            </div>
          )}
          {step === "property" && <PropertyStep data={data} set={set} onNext={() => { track("property_details_completed"); setStep("rooms"); }} />}
          {step === "rooms" && (
            <RoomsStep data={data} setRoom={setRoom} addRoom={addRoom} duplicateRoom={duplicateRoom} removeRoom={removeRoom} moveRoom={moveRoom}
              onBack={() => setStep("property")} onNext={() => setStep("style")} />
          )}
          {step === "style" && (
            <StyleStep data={data} set={set} onBack={() => setStep("rooms")} onSkip={runMatch} onNext={runMatch} loading={loading} />
          )}
          {step === "results" && activeStrategy && (
            <ResultsStep result={matchResult} activeStrategy={activeStrategy} activeKey={activeKey} onSelectStrategy={selectStrategy}
              displayedRooms={displayedRooms} summary={summary} onReplace={replaceMatch} onViewListing={(l) => window.open(`/listing/${l.id}`, "_blank")}
              onBack={() => setStep("style")} onNext={() => setStep("service")} />
          )}
          {step === "results" && !activeStrategy && (
            <NoMatchResults
              rooms={validRooms}
              onBack={() => setStep("rooms")}
              requestsEnabled={requestsCfg.enabled}
              onSendRequest={() => setShowRequest(true)}
              isDemo={isDemo}
            />
          )}
          {step === "service" && (
            <ServiceStep points={summary.points} onChoose={chooseService} onBack={() => setStep("results")} />
          )}
          {step === "requirements" && (
            <RequirementsStep service={service} reqs={reqs} setReqs={setReqs} loading={loading}
              onBack={() => setStep("service")} onNext={submitRequirements} />
          )}
          {step === "review" && (
            <ReviewStep data={data} displayedRooms={displayedRooms} summary={summary} service={service}
              serviceEstimate={serviceEstimate} serviceTotal={serviceTotal} reqs={reqs} savedRef={savedRef} loading={loading}
              onEditRooms={() => setStep("rooms")} onChangeProducts={() => setStep("results")} onChangeService={() => setStep("service")}
              onSave={saveProject} onReserve={(id) => { track("project_reservation_started"); nav(`/listing/${id}`); }}
              onPayAll={payForAll} onRequestFitter={() => requestFitter()} onRequestMeasuring={() => requestFitter({ measuring: true })}
              onSendRequest={() => setShowRequest(true)} isDemo={isDemo} requestsEnabled={requestsCfg.enabled} />
          )}
        </div>

        {/* Sticky mobile summary bar (results onward) */}
        {["results", "service", "requirements", "review"].includes(step) && summary.matched > 0 && (
          <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between" data-testid="mobile-summary-bar">
            <div>
              <div className="text-[11px] text-slate-500">{summary.matched}/{summary.total} rooms · {summary.points} collection{summary.points !== 1 ? "s" : ""}</div>
              <div className="font-display font-bold text-lg text-[#0F3D2E]">{gbp(serviceEstimate ? serviceTotal : summary.subtotal)}</div>
            </div>
            {summary.saving > 0 && (
              <div className="text-right">
                <div className="text-[11px] text-slate-500">Est. saving</div>
                <div className="font-display font-bold text-lg text-[#2A5A3B]">{gbp(summary.saving)}</div>
              </div>
            )}
          </div>
        )}
      </main>

      {showRequest && (
        <SendRequestModal
          rooms={validRooms.map((r) => ({
            name: r.name || "Room",
            width_m: Number(r.width_m),
            length_m: Number(r.length_m),
            flooring_type: r.flooring_type,
            colour: r.colour || null,
          }))}
          postcode={data.postcode}
          projectReference={savedRef}
          source="whole_home"
          onClose={() => setShowRequest(false)}
        />
      )}
    </>
  );
}

function StepBar({ step }) {
  const labels = [
    { k: "property", l: "Property" }, { k: "rooms", l: "Rooms" }, { k: "style", l: "Style" },
    { k: "results", l: "Matches" }, { k: "service", l: "Service" }, { k: "review", l: "Review" },
  ];
  const order = ["property", "rooms", "style", "results", "service", "requirements", "review"];
  const cur = order.indexOf(step);
  return (
    <div className="flex items-center gap-1.5 mt-4 overflow-x-auto" data-testid="whole-home-steps">
      {labels.map((s) => {
        const done = order.indexOf(s.k) < cur;
        const active = s.k === step || (s.k === "service" && step === "requirements");
        return (
          <div key={s.k} className={`flex-1 min-w-[52px] h-1.5 rounded-full ${active ? "bg-[#E05915]" : done ? "bg-[#9fe0b6]" : "bg-white/20"}`} title={s.l} />
        );
      })}
    </div>
  );
}

/* ---------------- Step 1: Property ---------------- */
function PropertyStep({ data, set, onNext }) {
  return (
    <div className="max-w-xl mx-auto bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid="step-property">
      <h2 className="font-display font-bold text-2xl text-slate-900 flex items-center gap-2"><Home className="w-6 h-6 text-[#2A5A3B]" /> What are you flooring?</h2>
      <div className="grid grid-cols-2 gap-2 mt-4">
        {PROPERTY_TYPES.map((p) => (
          <button key={p.value} onClick={() => set({ property_type: p.value })}
            className={`h-14 rounded-xl border font-semibold transition-colors ${data.property_type === p.value ? "border-[#E05915] bg-[#E05915]/10 text-[#E05915]" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"}`}
            data-testid={`property-${p.value}`}>{p.label}</button>
        ))}
      </div>
      <label className="block mt-5">
        <span className="fm-label">Your postcode <span className="text-slate-400 font-normal">(so we can match by distance)</span></span>
        <input className="fm-input" value={data.postcode} onChange={(e) => set({ postcode: e.target.value.toUpperCase() })} placeholder="M1 1AE" autoComplete="postal-code" data-testid="wh-postcode" />
      </label>
      <label className="block mt-4">
        <span className="fm-label">Project name <span className="text-slate-400 font-normal">(optional)</span></span>
        <input className="fm-input" value={data.project_name} onChange={(e) => set({ project_name: e.target.value })} placeholder="e.g. New house, Rental refurb" data-testid="wh-project-name" />
      </label>
      <button onClick={onNext} className="w-full h-13 py-3.5 mt-6 rounded-full bg-[#0F3D2E] hover:bg-[#0c3325] text-white font-semibold inline-flex items-center justify-center gap-2" data-testid="property-next">
        Add your rooms <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

/* ---------------- Step 2: Rooms ---------------- */
function RoomsStep({ data, setRoom, addRoom, duplicateRoom, removeRoom, moveRoom, onBack, onNext }) {
  // The most-recently added / edited room stays expanded; completed others
  // collapse to a tidy summary so the plan stays compact on a phone.
  const [activeId, setActiveId] = useState(null);
  return (
    <div className="max-w-2xl mx-auto" data-testid="step-rooms">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-2xl text-slate-900">Your flooring plan</h2>
        <span className="text-sm text-slate-500" data-testid="plan-room-count">
          {data.rooms.length} room{data.rooms.length !== 1 ? "s" : ""}
          {(() => {
            const a = data.rooms.reduce((s, r) => s + ((Number(r.width_m) > 0 && Number(r.length_m) > 0) ? Math.round(Number(r.width_m) * Number(r.length_m) * 100) / 100 : 0), 0);
            return a > 0 ? ` · ${a.toFixed(1)}m²` : "";
          })()}
        </span>
      </div>

      <div className="bg-[#2A5A3B]/5 border border-[#2A5A3B]/20 rounded-xl p-3 mt-3 text-sm text-slate-700 flex items-start gap-2">
        <Ruler className="w-4 h-4 mt-0.5 text-[#2A5A3B] shrink-0" />
        <span>Measure the longest width and length of each room, including alcoves and doorways. Matched to the measurements you provide — stairs and irregular rooms may need professional confirmation.</span>
      </div>

      <div className="space-y-3 mt-4">
        {data.rooms.map((room, i) => (
          <RoomEditor key={room.id} room={room} index={i} total={data.rooms.length}
            activeId={activeId} onEdit={setActiveId}
            setRoom={setRoom} duplicateRoom={duplicateRoom} removeRoom={removeRoom} moveRoom={moveRoom} />
        ))}
      </div>

      <button onClick={() => { const id = addRoom(); setActiveId(id); }} className="w-full h-12 mt-3 rounded-xl border-2 border-dashed border-slate-300 text-slate-600 font-semibold inline-flex items-center justify-center gap-2 hover:border-[#E05915] hover:text-[#E05915]" data-testid="add-room-btn">
        <Plus className="w-5 h-5" /> Add another room
      </button>

      <div className="flex flex-wrap gap-2 mt-6">
        <button onClick={onBack} className="h-12 px-5 rounded-full border border-slate-300 bg-white font-semibold text-sm text-slate-700 inline-flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back</button>
        <button onClick={onNext} className="flex-1 min-w-[180px] whitespace-nowrap h-12 rounded-full bg-[#0F3D2E] hover:bg-[#0c3325] text-white font-semibold inline-flex items-center justify-center gap-2" data-testid="rooms-next">
          Style preferences <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function RoomEditor({ room, index, total, activeId, onEdit, setRoom, duplicateRoom, removeRoom, moveRoom }) {
  const { unit } = useUnit();
  const complete = Number(room.width_m) > 0 && Number(room.length_m) > 0;
  // Collapse any completed room that isn't the one being actively edited, so
  // adding/editing another room tidies the previous cards automatically.
  const collapsed = complete && room.id !== activeId;
  const [open, setOpen] = useState(false);
  const area = complete ? (Number(room.width_m) * Number(room.length_m)).toFixed(2) : null;
  const isStairs = STAIRS_NAMES.includes((room.name || "").toLowerCase());

  // Completed rooms collapse to a tidy summary row so a whole-home plan stays
  // compact. Tap Edit to expand again.
  if (collapsed) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-3.5 flex items-center gap-3" data-testid={`room-editor-${room.id}`}>
        <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#2A5A3B]/10 text-[#2A5A3B] shrink-0">
          <CheckCircle2 className="w-4 h-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-slate-900 truncate" data-testid={`room-summary-name-${room.id}`}>{room.name || "Room"}</div>
          <div className="text-sm text-slate-500 truncate">{dimPairLabel(room.width_m, room.length_m, unit)} · {area}m² · {floorLabel(room.flooring_type)}</div>
        </div>
        <button onClick={() => onEdit(room.id)} className="text-sm font-semibold text-[#E05915] hover:underline shrink-0" data-testid={`edit-room-${room.id}`}>Edit</button>
        <button onClick={() => duplicateRoom(room.id)} className="text-slate-400 hover:text-[#2A5A3B] p-1" aria-label="Duplicate room" data-testid={`dup-room-${room.id}`}><Copy className="w-4 h-4" /></button>
        <button onClick={() => removeRoom(room.id)} disabled={total === 1} className="text-slate-400 hover:text-red-600 p-1 disabled:opacity-30" aria-label="Remove room" data-testid={`del-room-${room.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`room-editor-${room.id}`}>
      <div className="flex items-center gap-2">
        <div className="flex flex-col">
          <button onClick={() => moveRoom(room.id, -1)} disabled={index === 0} className="text-slate-300 hover:text-slate-600 disabled:opacity-30" aria-label="Move up"><GripVertical className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 min-w-0">
          <select
            className="w-full font-display font-semibold text-lg text-slate-900 bg-transparent focus:outline-none cursor-pointer"
            value={ROOM_NAMES.includes(room.name) ? room.name : "__custom__"}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__custom__") setRoom(room.id, { name: "", is_stairs: false });
              else setRoom(room.id, { name: v, is_stairs: STAIRS_NAMES.includes(v.toLowerCase()) });
            }}
            data-testid={`room-name-select-${room.id}`}
            aria-label="Room name"
          >
            {ROOM_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
            <option value="__custom__">Other — custom name</option>
          </select>
          {!ROOM_NAMES.includes(room.name) && (
            <input
              className="w-full mt-1 text-sm rounded-md border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30"
              value={room.name}
              onChange={(e) => setRoom(room.id, { name: e.target.value })}
              placeholder="Name this room"
              data-testid={`room-name-${room.id}`}
              aria-label="Custom room name"
            />
          )}
        </div>
        {area && <span className="text-sm text-slate-500 shrink-0">{area}m²</span>}
        <button onClick={() => duplicateRoom(room.id)} className="text-slate-400 hover:text-[#2A5A3B] p-1" aria-label="Duplicate room" data-testid={`dup-room-${room.id}`}><Copy className="w-4 h-4" /></button>
        <button onClick={() => removeRoom(room.id)} disabled={total === 1} className="text-slate-400 hover:text-red-600 p-1 disabled:opacity-30" aria-label="Remove room" data-testid={`del-room-${room.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>

      <div className="flex items-center justify-between mt-3">
        <span className="fm-label !mb-0">Room size</span>
        <UnitToggle />
      </div>
      <div className="grid grid-cols-2 gap-3 mt-2">
        <label className="block">
          <span className="fm-label">Width ({unitLabel(unit)})</span>
          <input className="fm-input" type="number" inputMode="decimal" step="any" min="0" value={mToUnit(room.width_m, unit)} onChange={(e) => setRoom(room.id, { width_m: unitToM(e.target.value, unit) ?? "" })} data-testid={`room-width-${room.id}`} />
        </label>
        <label className="block">
          <span className="fm-label">Length ({unitLabel(unit)})</span>
          <input className="fm-input" type="number" inputMode="decimal" step="any" min="0" value={mToUnit(room.length_m, unit)} onChange={(e) => setRoom(room.id, { length_m: unitToM(e.target.value, unit) ?? "" })} data-testid={`room-length-${room.id}`} />
        </label>
      </div>
      <label className="block mt-3">
        <span className="fm-label">Preferred flooring</span>
        <select className="fm-input" value={room.flooring_type} onChange={(e) => setRoom(room.id, { flooring_type: e.target.value })} data-testid={`room-type-${room.id}`}>
          {FLOORING_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </label>

      {isStairs && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2" data-testid={`stairs-note-${room.id}`}>
          <Ruler className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Stairs aren't a simple rectangle. Enter your best estimate of the carpeted run — a fitter will confirm the exact measurement before anything is guaranteed to fit.</span>
        </div>
      )}

      {open && (
        <div className="grid grid-cols-2 gap-3 mt-3">
          <label className="block">
            <span className="fm-label">Max budget (£) <span className="text-slate-400 font-normal">opt.</span></span>
            <input className="fm-input" type="number" inputMode="numeric" min="0" value={room.budget_gbp} onChange={(e) => setRoom(room.id, { budget_gbp: e.target.value })} data-testid={`room-budget-${room.id}`} />
          </label>
          <label className="block">
            <span className="fm-label">Colour <span className="text-slate-400 font-normal">opt.</span></span>
            <select className="fm-input" value={room.colour} onChange={(e) => setRoom(room.id, { colour: e.target.value })} data-testid={`room-colour-${room.id}`}>
              <option value="">No preference</option>
              {COLOUR_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        </div>
      )}
      <div className="flex items-center justify-between mt-2">
        <button onClick={() => setOpen((o) => !o)} className="text-xs text-slate-500 underline">{open ? "Fewer options" : "More options (budget, colour)"}</button>
        {complete && (
          <button onClick={() => onEdit(null)} className="text-xs font-semibold text-[#2A5A3B] inline-flex items-center gap-1" data-testid={`done-room-${room.id}`}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Done
          </button>
        )}
      </div>

      {isStairs && (
        <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
          Stairs can't be matched from a simple square-metre figure — results are indicative and need professional confirmation.
        </p>
      )}
    </div>
  );
}

/* ---------------- Step 3: Style ---------------- */
function StyleStep({ data, set, onBack, onSkip, onNext, loading }) {
  const p = data.preferences;
  const setP = (patch) => set({ preferences: { ...p, ...patch } });
  return (
    <div className="max-w-xl mx-auto bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid="step-style">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-2xl text-slate-900 flex items-center gap-2"><Sparkles className="w-6 h-6 text-[#E05915]" /> Style preferences</h2>
        <span className="text-xs text-slate-400">Optional</span>
      </div>
      <p className="text-sm text-slate-500 mt-1">These guide ranking — they're filters, not guarantees. Skip if you like.</p>

      <span className="fm-label mt-4 block">What matters most?</span>
      <div className="grid gap-2 mt-1">
        {STYLE_GOALS.map((g) => (
          <button key={g.value} onClick={() => setP({ style_goal: g.value })}
            className={`text-left p-3.5 rounded-xl border font-medium transition-colors ${p.style_goal === g.value ? "border-[#E05915] bg-[#E05915]/5 text-slate-900" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"}`}
            data-testid={`style-goal-${g.value}`}>{g.label}</button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <label className="block">
          <span className="fm-label">Preferred colour</span>
          <select className="fm-input" value={p.colour} onChange={(e) => setP({ colour: e.target.value })} data-testid="style-colour">
            <option value="">Any colour</option>
            {COLOUR_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="fm-label">Finish</span>
          <select className="fm-input" value={p.texture} onChange={(e) => setP({ texture: e.target.value })} data-testid="style-texture">
            <option value="">Any finish</option>
            {TEXTURE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
      </div>

      <div className="flex flex-wrap gap-2 mt-6">
        <button onClick={onBack} className="h-12 px-5 rounded-full border border-slate-300 bg-white font-semibold text-sm text-slate-700 inline-flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back</button>
        <button onClick={onSkip} className="h-12 px-4 rounded-full border border-slate-300 bg-white font-semibold text-sm text-slate-600" data-testid="style-skip">Skip</button>
        <button onClick={onNext} disabled={loading} className="flex-1 min-w-[200px] whitespace-nowrap h-12 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70" data-testid="style-match">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Matching…</> : <>{(data.rooms.filter((r) => Number(r.width_m) > 0 && Number(r.length_m) > 0).length <= 1) ? "Find flooring for my room" : "Find flooring for my rooms"} <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
    </div>
  );
}

/* ---------------- Step 4: Results ---------------- */
function ResultsStep({ result, activeStrategy, activeKey, onSelectStrategy, displayedRooms, summary, onReplace, onViewListing, onBack, onNext }) {
  return (
    <div data-testid="step-results">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900">Your whole-home FloorMatch</h2>
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-800 underline">Edit measurements</button>
      </div>
      <p className="text-slate-500 text-sm mt-1">Different rooms. Carefully matched flooring. One organised project.</p>

      {/* Strategy tabs */}
      <div className="flex gap-2 mt-4 overflow-x-auto pb-1" data-testid="strategy-tabs">
        {result.strategies.map((s) => (
          <button key={s.key} onClick={() => onSelectStrategy(s.key)}
            className={`shrink-0 text-left px-4 py-3 rounded-xl border transition-colors ${activeKey === s.key ? "border-[#E05915] bg-[#E05915]/5" : "border-slate-200 bg-white hover:border-slate-300"}`}
            data-testid={`strategy-${s.key}`}>
            <div className="font-display font-semibold text-slate-900">{s.title}</div>
            <div className="text-xs text-slate-500">{s.subtitle}</div>
            <div className="text-xs mt-1"><span className="font-bold text-[#2A5A3B]">{gbp(s.saving_gbp)} saved</span> · {s.collection_points} collection{s.collection_points !== 1 ? "s" : ""}</div>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-6 mt-5 items-start">
        {/* Room matches */}
        <div className="lg:col-span-8 space-y-4">
          {summary.matched < summary.total && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm">
              {summary.total - summary.matched} room(s) have no suitable match in this strategy. Try another strategy, widen the flooring type, or edit the size.
            </div>
          )}
          {displayedRooms.map((room) => (
            <RoomMatchCard key={room.room_id} room={room} onReplace={onReplace} onViewListing={onViewListing} />
          ))}
        </div>

        {/* Sticky summary */}
        <aside className="lg:col-span-4 lg:sticky lg:top-24">
          <SummaryPanel summary={summary} />
          <button onClick={onNext} disabled={summary.matched === 0} className="w-full h-13 py-3.5 mt-3 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="results-continue">
            Choose delivery & fitting <ArrowRight className="w-4 h-4" />
          </button>
        </aside>
      </div>
    </div>
  );
}

function SummaryPanel({ summary, serviceEstimate, serviceTotal }) {
  // Integer-pound arithmetic so displayed components always add to totals.
  const flooringR = Math.round(summary.subtotal || 0);
  const retailR = Math.round(summary.retail || 0);
  const savingR = Math.max(0, retailR - flooringR);
  const savingPctR = retailR > 0 ? Math.round((savingR / retailR) * 100) : 0;
  const hasService = serviceEstimate && serviceEstimate.estimate_gbp > 0;
  const lines = hasService ? serviceEstimate.lines.map((l) => ({ label: l.label, amt: Math.round(l.amount) })) : [];
  const serviceR = lines.reduce((a, l) => a + l.amt, 0);
  const projectR = flooringR + serviceR;
  const showProject = !!serviceEstimate; // review stage
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="summary-panel">
      <h3 className="font-display font-bold text-lg text-slate-900">
        {showProject ? "Your project estimate" : "Your whole-home FloorMatch"}
      </h3>
      <p className="text-xs text-slate-500 mt-0.5">{summary.matched}/{summary.total} rooms · {summary.area.toFixed(1)}m² · {summary.points} collection{summary.points !== 1 ? "s" : ""}</p>

      {/* FLOORING COMPARISON */}
      <div className="mt-4">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Flooring comparison</p>
        <dl className="mt-1.5 space-y-1.5 text-sm">
          <Row k="Estimated typical retail flooring" v={gbp(retailR)} />
          <Row k="FloorMatch flooring" v={gbp(flooringR)} />
        </dl>
        {savingR > 0 && (
          <div className="mt-2.5 rounded-xl bg-[#2A5A3B] text-white p-3.5" data-testid="flooring-saving">
            <div className="text-[11px] text-[#9fe0b6] uppercase tracking-wider font-semibold">Indicative flooring saving</div>
            <div className="font-display font-bold text-2xl mt-0.5">{gbp(savingR)} <span className="text-base font-semibold">({savingPctR}%)</span></div>
          </div>
        )}
      </div>

      {/* SERVICE ESTIMATE */}
      {hasService && (
        <div className="mt-4 pt-4 border-t border-slate-100" data-testid="service-estimate-group">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Service estimate</p>
          <dl className="mt-1.5 space-y-1.5 text-sm">
            {lines.map((l) => <Row key={l.label} k={l.label} v={gbp(l.amt)} />)}
            <Row k="Estimated service total" v={gbp(serviceR)} bold />
          </dl>
        </div>
      )}
      {serviceEstimate && !hasService && (
        <div className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-500" data-testid="service-estimate-group">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Service estimate</p>
          {serviceEstimate.note || "You collect the flooring yourself — no service cost."}
        </div>
      )}

      {/* PROJECT ESTIMATE */}
      {showProject && (
        <div className="mt-4 pt-4 border-t border-slate-100" data-testid="project-estimate-group">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Project estimate</p>
          <dl className="mt-1.5 space-y-1.5 text-sm">
            <Row k="Flooring subtotal" v={gbp(flooringR)} />
            <Row k="Estimated services" v={gbp(serviceR)} />
            <Row k="Estimated FloorMatch project total" v={gbp(projectR)} bold />
          </dl>
        </div>
      )}

      <p className="mt-3 text-[11px] text-slate-500 leading-snug">
        The indicative saving compares flooring prices only. Delivery and fitting costs are shown
        separately because service prices vary by property and job requirements.
      </p>
      <details className="mt-2 text-xs text-slate-500">
        <summary className="cursor-pointer">How is the comparison worked out?</summary>
        <p className="mt-1">Comparison uses a transparent estimated typical retail price per m² for each flooring type over the area your rooms need. Estimates are clearly labelled and are not a guaranteed saving.</p>
      </details>
    </div>
  );
}
function Row({ k, v, bold }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-slate-500">{k}</dt>
      <dd className={`text-slate-900 ${bold ? "font-display font-bold text-base" : "font-semibold"}`}>{v}</dd>
    </div>
  );
}

/* ---------------- Step 5: Service ---------------- */
function ServiceStep({ points, onChoose, onBack }) {
  return (
    <div className="max-w-3xl mx-auto" data-testid="step-service">
      <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900">How would you like to receive and install your flooring?</h2>
      {points > 1 && (
        <p className="text-sm text-slate-600 bg-slate-100 rounded-lg px-3 py-2 mt-3">
          Your products come from {points} shops, so a collection service may include multiple collections.
        </p>
      )}
      <div className="grid sm:grid-cols-2 gap-3 mt-5">
        {SERVICE_OPTIONS.map((s) => {
          const Icon = SERVICE_ICON[s.value];
          return (
            <button key={s.value} onClick={() => onChoose(s.value)}
              className="text-left bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#E05915] hover:shadow-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E05915]"
              data-testid={`service-${s.value}`}>
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#0F3D2E] text-white"><Icon className="w-5 h-5" /></span>
              <h3 className="font-display font-semibold text-lg text-slate-900 mt-3">{s.title}</h3>
              <p className="text-sm text-slate-600 mt-1">{s.body}</p>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-slate-400 mt-4">You'll review the confirmed service price before accepting — nothing is booked yet.</p>
      <button onClick={onBack} className="h-12 px-5 mt-5 rounded-full border border-slate-300 bg-white font-semibold text-sm text-slate-700 inline-flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back to matches</button>
    </div>
  );
}

/* ---------------- Step 6: Requirements ---------------- */
const DELIVERY_FIELDS = [
  { key: "floor_level", type: "select", label: "Floor level", options: ["Ground", "1st", "2nd", "3rd+"] },
  { key: "lift_available", type: "yesno", label: "Lift available?" },
  { key: "help_carrying", type: "yesno", label: "Help carrying indoors?" },
  { key: "multiple_collections", type: "yesno", label: "Multiple collections expected?" },
];
const FITTING_FIELDS = [
  { key: "property_occupied", type: "yesno", label: "Property occupied?" },
  { key: "furniture_present", type: "yesno", label: "Furniture present?" },
  { key: "uplift_required", type: "yesno", label: "Uplift existing flooring?" },
  { key: "disposal_required", type: "yesno", label: "Disposal required?" },
  { key: "underlay_required", type: "yesno", label: "Underlay required?" },
  { key: "grippers_required", type: "yesno", label: "Grippers required?" },
  { key: "door_bars_required", type: "yesno", label: "Door bars required?" },
  { key: "door_trimming", type: "yesno", label: "Doors likely need trimming?" },
];
function RequirementsStep({ service, reqs, setReqs, loading, onBack, onNext }) {
  const showDelivery = service === "collection_delivery" || service === "collection_delivery_fitting";
  const showFitting = service === "fitting" || service === "collection_delivery_fitting";
  const setR = (k, v) => setReqs((r) => ({ ...r, [k]: v }));
  return (
    <div className="max-w-xl mx-auto" data-testid="step-requirements">
      <h2 className="font-display font-bold text-2xl text-slate-900">A few service details</h2>
      <p className="text-sm text-slate-500 mt-1">Only what's relevant to your choice. Nothing is booked yet.</p>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 mt-4 space-y-4">
        <label className="block">
          <span className="fm-label">Preferred date or range</span>
          <input className="fm-input" value={reqs.preferred_date || ""} onChange={(e) => setR("preferred_date", e.target.value)} placeholder="e.g. w/c 23 June, or any weekday" data-testid="req-date" />
        </label>

        {showDelivery && (
          <div className="pt-2 border-t border-slate-100" data-testid="delivery-fields">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Delivery</p>
            {DELIVERY_FIELDS.map((f) => <ReqField key={f.key} f={f} value={reqs[f.key]} onChange={(v) => setR(f.key, v)} />)}
            <label className="block mt-2">
              <span className="fm-label">Access / parking notes</span>
              <input className="fm-input" value={reqs.access_notes || ""} onChange={(e) => setR("access_notes", e.target.value)} data-testid="req-access" />
            </label>
            <label className="block mt-2">
              <span className="fm-label">Max flooring roll length (m)</span>
              <input className="fm-input" type="number" inputMode="decimal" step="0.5" value={reqs.max_roll_length_m || ""} onChange={(e) => setR("max_roll_length_m", e.target.value)} data-testid="req-roll" />
            </label>
          </div>
        )}

        {showFitting && (
          <div className="pt-2 border-t border-slate-100" data-testid="fitting-fields">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Fitting</p>
            {FITTING_FIELDS.map((f) => <ReqField key={f.key} f={f} value={reqs[f.key]} onChange={(v) => setR(f.key, v)} />)}
            <label className="block mt-2">
              <span className="fm-label">Subfloor concerns / notes</span>
              <input className="fm-input" value={reqs.subfloor_notes || ""} onChange={(e) => setR("subfloor_notes", e.target.value)} data-testid="req-subfloor" />
            </label>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mt-6">
        <button onClick={onBack} className="h-12 px-5 rounded-full border border-slate-300 bg-white font-semibold text-sm text-slate-700 inline-flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back</button>
        <button onClick={onNext} disabled={loading} className="flex-1 min-w-[200px] whitespace-nowrap h-12 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70" data-testid="req-next">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Estimating…</> : <>See estimate & review <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
    </div>
  );
}
function ReqField({ f, value, onChange }) {
  if (f.type === "yesno") {
    return (
      <div className="flex items-center justify-between py-2">
        <span className="text-sm text-slate-700">{f.label}</span>
        <div className="flex gap-1.5">
          {["Yes", "No"].map((o) => {
            const v = o === "Yes";
            return (
              <button key={o} onClick={() => onChange(v)} className={`h-9 px-4 rounded-full text-sm font-semibold border ${value === v ? "border-[#0F3D2E] bg-[#0F3D2E]/10 text-[#0F3D2E]" : "border-slate-300 text-slate-600"}`} data-testid={`req-${f.key}-${o.toLowerCase()}`}>{o}</button>
            );
          })}
        </div>
      </div>
    );
  }
  return (
    <label className="block py-1.5">
      <span className="fm-label">{f.label}</span>
      <select className="fm-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`req-${f.key}`}>
        <option value="">Select</option>
        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

/* ---------------- Step 7: Review ---------------- */
function NoMatchResults({ rooms, onBack, requestsEnabled, onSendRequest, isDemo }) {
  const { unit } = useUnit();
  return (
    <div data-testid="step-results">
      <div className="bg-white border border-amber-300 rounded-2xl p-6" data-testid="wholehome-nomatch">
        <h2 className="font-display font-bold text-2xl text-slate-900">No matches just yet</h2>
        <p className="text-slate-600 mt-2 text-sm">
          We couldn't find surplus stock that fits your plan right now. Manchester
          surplus lands daily — widen your distance or let nearby retailers know what you're after.
        </p>
        <div className="mt-4 space-y-2" data-testid="nomatch-rooms">
          {rooms.map((r) => (
            <div key={r.id} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
              <span className="font-semibold text-slate-800">{r.name || "Room"}</span>
              <span className="text-slate-500">{dimPairLabel(r.width_m, r.length_m, unit)} · {floorLabel(r.flooring_type)}</span>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 mt-6">
          <button onClick={onBack} className="h-11 px-5 rounded-full border border-slate-300 bg-white hover:bg-slate-50 font-semibold text-slate-800 inline-flex items-center gap-2" data-testid="nomatch-edit-rooms">
            <ArrowLeft className="w-4 h-4" /> Edit rooms
          </button>
          {requestsEnabled && (
            <button onClick={onSendRequest} disabled={isDemo} className="h-11 px-5 rounded-full bg-[#2A5A3B] hover:bg-[#23492f] text-white font-semibold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="nomatch-send-request">
              Send request to nearby retailers
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewStep({ data, displayedRooms, summary, service, serviceEstimate, serviceTotal, reqs, savedRef, loading, onEditRooms, onChangeProducts, onChangeService, onSave, onReserve, onPayAll, onRequestFitter, onRequestMeasuring, onSendRequest, isDemo, requestsEnabled }) {
  const { unit } = useUnit();
  const serviceLabel = SERVICE_OPTIONS.find((s) => s.value === service)?.title || "Not selected";
  const firstMatch = displayedRooms.find((r) => r.match)?.match?.listing;
  const matchedCount = displayedRooms.filter((r) => r.match).length;
  const needsFitter = ["fitting", "collection_delivery", "collection_delivery_fitting"].includes(service);
  return (
    <div className="grid lg:grid-cols-12 gap-6 items-start" data-testid="step-review">
      <div className="lg:col-span-8 space-y-4">
        <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900">Review your project</h2>
        <p className="text-sm text-slate-500">{data.project_name || "Your project"} · {PROPERTY_TYPES.find((p) => p.value === data.property_type)?.label} · {data.postcode || "postcode not set"}</p>

        <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
          {displayedRooms.map((r) => (
            <div key={r.room_id} className="p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900">{r.room_name} <span className="text-slate-400 font-normal">· {dimPairLabel(r.width_m, r.length_m, unit)}</span></div>
                <div className="text-sm text-slate-500 truncate">{r.match ? `${r.match.listing.title} (${floorLabel(r.match.listing.flooring_type)})` : "No match"}</div>
              </div>
              <div className="text-right shrink-0">
                {r.match && <div className="font-bold text-[#0F3D2E]">{gbp(r.match.room_cost_gbp)}</div>}
                {r.collection_point && <div className="text-[11px] text-slate-400">Point {r.collection_point}</div>}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-semibold text-slate-900">Chosen service</h3>
            <button onClick={onChangeService} className="text-sm text-[#E05915] underline" data-testid="review-change-service">Change</button>
          </div>
          <p className="text-slate-700 mt-1">{serviceLabel}</p>
          {serviceEstimate ? (
            <p className="text-[11px] text-slate-400 mt-1">{serviceEstimate.note}</p>
          ) : <p className="text-sm text-amber-700 mt-1">Price confirmed before booking.</p>}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Matched to the measurements you provide. Stock availability and the confirmed service price are checked before anything is booked. Stairs and irregular rooms need professional confirmation.</span>
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={onEditRooms} className="h-11 px-4 rounded-full border border-slate-300 bg-white font-semibold text-sm text-slate-700" data-testid="review-edit-rooms">Edit rooms</button>
          <button onClick={onChangeProducts} className="h-11 px-4 rounded-full border border-slate-300 bg-white font-semibold text-sm text-slate-700" data-testid="review-change-products">Change products</button>
        </div>
      </div>

      <aside className="lg:col-span-4 lg:sticky lg:top-24">
        <SummaryPanel summary={summary} serviceEstimate={serviceEstimate} serviceTotal={serviceTotal} />
        <div className="mt-3 space-y-2">
          <button onClick={onSave} disabled={loading} className="w-full h-12 rounded-full bg-[#0F3D2E] hover:bg-[#0c3325] text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70" data-testid="review-save">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {savedRef ? "Update saved project" : "Save project"}
          </button>
          {matchedCount > 0 && (
            <button onClick={onPayAll} disabled={loading || isDemo} className="w-full h-12 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed" data-testid="review-pay-all">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageCheck className="w-4 h-4" />} Reserve &amp; pay for all {matchedCount > 1 ? `${matchedCount} rooms` : "my room"}
            </button>
          )}
          {firstMatch && (
            <button onClick={() => onReserve(firstMatch.id)} className="w-full h-11 rounded-full border border-slate-300 bg-white font-semibold text-sm text-slate-700 inline-flex items-center justify-center gap-2" data-testid="review-reserve">
              Reserve one item at a time <ArrowRight className="w-4 h-4" />
            </button>
          )}
          {savedRef && <p className="text-xs text-center text-slate-500">Saved as <span className="font-mono font-semibold">{savedRef}</span> — keep this to return later.</p>}
          {savedRef && !isDemo && <EmailResumeLink reference={savedRef} />}
          <p className="text-[11px] text-slate-400 text-center">{isDemo ? "Paid checkout is disabled in this demonstration." : "Pay once for every shop in your plan — we split the payment to each retailer behind the scenes. Collect from each shop with your order reference."}</p>

          {needsFitter && (
            <div className="pt-3 mt-1 border-t border-slate-100">
              <button onClick={onRequestFitter} disabled={isDemo || loading} className="w-full h-11 rounded-full bg-[#0F3D2E] hover:bg-[#0c3325] text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-testid="review-request-fitter">
                <Wrench className="w-4 h-4" /> Request a fitter for this job
              </button>
              <p className="text-[11px] text-slate-400 text-center mt-1.5">{isDemo ? "Fitter requests are disabled in this demonstration." : "We'll offer your job to approved local fitters. Your identity and exact address stay private until a fitter is confirmed."}</p>
            </div>
          )}

          <div className="pt-3 mt-1 border-t border-slate-100">
            <button onClick={onRequestMeasuring} disabled={isDemo || loading} className="w-full h-11 rounded-full border border-[#0F3D2E]/30 bg-[#0F3D2E]/5 hover:bg-[#0F3D2E]/10 text-[#0F3D2E] font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-testid="review-request-measuring">
              <Ruler className="w-4 h-4" /> Request a measuring service
            </button>
            <p className="text-[11px] text-slate-400 text-center mt-1.5">{isDemo ? "Measuring requests are disabled in this demonstration." : "Not sure of your exact sizes, or have stairs or awkward rooms? A local professional can measure up for you. We'll offer it to nearby fitters — your details stay private until one is confirmed."}</p>
          </div>

          {requestsEnabled && (
            <div className="pt-3 mt-1 border-t border-slate-100">
              <button onClick={onSendRequest} disabled={isDemo} className="w-full h-11 rounded-full border border-[#2A5A3B]/30 bg-[#2A5A3B]/5 hover:bg-[#2A5A3B]/10 text-[#2A5A3B] font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-testid="review-send-request">
                <HandHeart className="w-4 h-4" /> Send request to nearby retailers
              </button>
              <p className="text-[11px] text-slate-400 text-center mt-1.5">{isDemo ? "Requests are disabled in this demonstration." : "Can't find the perfect match? Let nearby retailers know what you're looking for — your details stay private."}</p>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
